import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config';
import { FiMail, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { sanitizeEmail, sanitizeText, validators } from '../utils/sanitize';
import { useThemeClasses } from '../hooks/useThemeClasses';
import { FormField, FormAlert, formInputClass } from '../components/form';
import AuthSplitShell from '../components/auth/AuthSplitShell';
import TransactionalEmailHint from '../components/auth/TransactionalEmailHint';

const ForgotPassword = () => {
  const tc = useThemeClasses();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const sanitizedEmail = sanitizeEmail(email);

      const emailValidation = validators.email(sanitizedEmail);
      if (!emailValidation.valid) {
        setError(emailValidation.error);
        setLoading(false);
        return;
      }

      const res = await fetch(apiUrl('/api/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sanitizedEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || 'No pudimos procesar tu pedido. Probá de nuevo en unos minutos.');
      }
      setSuccess(
        data.message ||
          'Si el email está registrado, te enviamos un enlace para elegir una nueva contraseña.'
      );
    } catch (err) {
      setError(sanitizeText(err.message) || 'Error de conexión. Verificá tu conexión a internet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell>
      <div className="mb-8">
        <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${tc.textPri}`}>
          ¿Olvidaste tu contraseña?
        </h2>
        <p className={`mt-2 text-sm ${tc.textMuted}`}>
          Ingresá tu email y te vamos a enviar un enlace para elegir una nueva.
        </p>
      </div>

      {error && <FormAlert type="error">{sanitizeText(error)}</FormAlert>}
      {success && (
        <>
          <FormAlert type="success">{sanitizeText(success)}</FormAlert>
          <TransactionalEmailHint className="mb-4" />
        </>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <FormField label="Correo electrónico" required labelClass={tc.labelClass}>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiMail className={`h-4 w-4 ${tc.textMuted}`} />
            </div>
            <input
              type="email"
              className={`${formInputClass(tc.inputClass)} pl-9`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="tu@email.com"
            />
          </div>
        </FormField>

        <button
          type="submit"
          className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${tc.btnPrimary}`}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Enviando enlace...
            </>
          ) : (
            <>
              Enviarme el enlace
              <FiArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </form>

      <p className={`mt-6 text-sm ${tc.textSec}`}>
        <Link to="/login" className="inline-flex items-center gap-1.5 font-medium text-[#1F80FF] hover:underline">
          <FiArrowLeft className="w-3.5 h-3.5" />
          Volver a iniciar sesión
        </Link>
      </p>
    </AuthSplitShell>
  );
};

export default ForgotPassword;
